/**
 * ConnectionLine — First-Class Interactive Connection on the Map
 * ──────────────────────────────────────────────────────────────
 * Extracted from CosmicMap. Renders a curved connection with 7 layers:
 *   1. Ambient pulse (cross-zone connections breathe)
 *   2. Glow bloom (highlighted/hovered connections)
 *   3. Invisible hit area (fat click target)
 *   4. Visible line (gradient for cross-zone)
 *   5. Energy flow particles (cross-zone when highlighted)
 *   6. Pluck ripple (on click animation)
 *   7. Ringing afterglow (post-pluck fade)
 *   + Hover tooltip indicator at midpoint
 *
 * Place at: src/components/map/ConnectionLine.jsx
 */

import React from 'react';
import { LINE_HIT_AREA, PLUCK_DURATION, RIPPLE_COUNT } from './mapConstants';

const ConnectionLine = ({
  sourceNode, targetNode, connectionId,
  isCrossZone, isHighlighted, isDimmed, isHovered,
  pluckState, onClick, onMouseEnter, onMouseLeave,
}) => {
  if (!sourceNode || !targetNode) return null;

  const dx = targetNode.x - sourceNode.x;
  const dy = targetNode.y - sourceNode.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;

  const curvature = Math.min(dist * 0.12, 40);
  const mx = (sourceNode.x + targetNode.x) / 2;
  const my = (sourceNode.y + targetNode.y) / 2;
  const nx = -dy / dist * curvature;
  const ny = dx / dist * curvature;
  const pathD = `M ${sourceNode.x} ${sourceNode.y} Q ${mx + nx} ${my + ny} ${targetNode.x} ${targetNode.y}`;

  const sourceColor = sourceNode.zone?.color || '#52525b';
  const targetColor = targetNode.zone?.color || sourceColor;
  const gradId = `cg-${connectionId}`;

  let strokeWidth, opacity, showGradient, showGlow, showAmbientPulse, showParticles;

  if (isDimmed) {
    // 0.03 erased dimmed lines entirely, so selecting one node made the
    // rest of the constellation look like unconnected dots. Dimming
    // should push context back, not delete it.
    strokeWidth = 0.6; opacity = 0.08;
    showGradient = false; showGlow = false; showAmbientPulse = false; showParticles = false;
  } else if (isHighlighted) {
    strokeWidth = isCrossZone ? 3.5 : 2.5; opacity = 0.95;
    showGradient = isCrossZone; showGlow = true; showAmbientPulse = isCrossZone; showParticles = isCrossZone;
  } else if (isHovered) {
    strokeWidth = isCrossZone ? 2.5 : 2; opacity = isCrossZone ? 0.7 : 0.55;
    showGradient = isCrossZone; showGlow = isCrossZone; showAmbientPulse = false; showParticles = false;
  } else if (isCrossZone) {
    // SEPTEMBER 2026 — RAISED FROM 0.3 / 1.8.
    //
    // Cross-zone connections are the product. A poem forking into a
    // circuit board is the thing this platform exists to make visible,
    // and at 0.3 on a zinc-950 background it was a smudge people had to
    // be told was there. On a projector -- which is how the Cabrillo demo
    // gets seen -- it was invisible.
    strokeWidth = 2.2; opacity = 0.55;
    showGradient = true; showGlow = false; showAmbientPulse = true; showParticles = false;
  } else {
    // SEPTEMBER 2026 — RAISED FROM 0.12.
    //
    // 0.12 on #09090b is roughly a #1a1a1d line: technically drawn,
    // practically absent. A same-zone fork looked like two unrelated
    // nodes that happened to sit near each other, and the first live
    // fork test read as "the connection did not save" when in fact it
    // had.
    //
    // THE HIERARCHY IS PRESERVED, WHICH IS THE POINT. Same-zone still
    // sits well below cross-zone (0.32 vs 0.55, 1.4 vs 2.2 stroke), so
    // an unexpected disciplinary jump still visibly outranks an expected
    // one. The floor was raised, not the distinction flattened.
    strokeWidth = 1.4; opacity = 0.32;
    showGradient = false; showGlow = false; showAmbientPulse = false; showParticles = false;
  }

  const isPlucking = pluckState === 'plucking';
  const isRinging = pluckState === 'ringing';

  return (
    <g style={{ transition: isDimmed ? 'opacity 0.5s' : 'none' }}>
      {showGradient && (
        <defs>
          <linearGradient id={gradId} gradientUnits="userSpaceOnUse"
            x1={sourceNode.x} y1={sourceNode.y} x2={targetNode.x} y2={targetNode.y}>
            <stop offset="0%" stopColor={sourceColor} />
            <stop offset="100%" stopColor={targetColor} />
          </linearGradient>
        </defs>
      )}

      {/* LAYER 1: Ambient pulse */}
      {showAmbientPulse && !isDimmed && (
        <path d={pathD} fill="none"
          stroke={showGradient ? `url(#${gradId})` : sourceColor}
          strokeWidth={strokeWidth + 6} opacity={0} style={{ pointerEvents: 'none' }}>
          <animate attributeName="opacity" values="0;0.06;0" dur="4s" repeatCount="indefinite" />
        </path>
      )}

      {/* LAYER 2: Glow bloom */}
      {showGlow && !isDimmed && (
        <>
          <path d={pathD} fill="none"
            stroke={showGradient ? `url(#${gradId})` : sourceColor}
            strokeWidth={strokeWidth + 8}
            opacity={isHighlighted ? 0.08 : 0.04}
            filter="url(#crossGlow)"
            style={{ pointerEvents: 'none' }} />
          <path d={pathD} fill="none"
            stroke={showGradient ? `url(#${gradId})` : sourceColor}
            strokeWidth={strokeWidth + 3}
            opacity={isHighlighted ? 0.2 : 0.1}
            style={{ pointerEvents: 'none' }} />
        </>
      )}

      {/* LAYER 3: Hit area */}
      <path d={pathD} fill="none" stroke="transparent" strokeWidth={LINE_HIT_AREA}
        strokeLinecap="round" style={{ cursor: 'pointer' }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave} />

      {/* LAYER 4: Visible line */}
      <path d={pathD} fill="none"
        stroke={showGradient ? `url(#${gradId})` : sourceColor}
        strokeWidth={strokeWidth} strokeLinecap="round" opacity={opacity}
        style={{ pointerEvents: 'none', transition: 'stroke-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />

      {/* LAYER 5: Energy flow particles */}
      {showParticles && !isDimmed && (
        <>
          <circle r={3} fill={sourceColor} opacity={0.9}>
            <animateMotion dur="2.5s" repeatCount="indefinite" path={pathD} />
            <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.5s" repeatCount="indefinite" />
            <animate attributeName="r" values="3;1.5;3" dur="2.5s" repeatCount="indefinite" />
          </circle>
          <circle r={2} fill={targetColor} opacity={0.6}>
            <animateMotion dur="2.5s" repeatCount="indefinite" path={pathD} begin="1.25s" />
            <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2.5s" repeatCount="indefinite" begin="1.25s" />
          </circle>
        </>
      )}

      {/* LAYER 6: Pluck ripple */}
      {isPlucking && (
        <>
          <circle r={5} fill="white" opacity={0.9}>
            <animateMotion dur={`${PLUCK_DURATION * 0.6}ms`} repeatCount="1" path={pathD} fill="freeze" />
            <animate attributeName="r" values="5;2" dur={`${PLUCK_DURATION * 0.6}ms`} fill="freeze" />
            <animate attributeName="opacity" values="0.9;0" dur={`${PLUCK_DURATION * 0.6}ms`} fill="freeze" />
          </circle>
          {[...Array(RIPPLE_COUNT)].map((_, i) => (
            <circle key={`ripple-${connectionId}-${i}`}
              r={4 - i} fill={i === 0 ? sourceColor : targetColor} opacity={0.7 - i * 0.15}>
              <animateMotion dur={`${PLUCK_DURATION * 0.7}ms`} repeatCount="1" path={pathD} begin={`${i * 80}ms`} fill="freeze" />
              <animate attributeName="opacity" values={`${0.7 - i * 0.15};0`} dur={`${PLUCK_DURATION * 0.7}ms`} begin={`${i * 80}ms`} fill="freeze" />
            </circle>
          ))}
          <path d={pathD} fill="none" stroke="white" strokeWidth={strokeWidth + 2} opacity={0.4} style={{ pointerEvents: 'none' }}>
            <animate attributeName="opacity" values="0.4;0;0.2;0;0.1;0" dur={`${PLUCK_DURATION}ms`} repeatCount="1" fill="freeze" />
          </path>
        </>
      )}

      {/* LAYER 7: Ringing afterglow */}
      {isRinging && (
        <path d={pathD} fill="none"
          stroke={showGradient ? `url(#${gradId})` : sourceColor}
          strokeWidth={strokeWidth + 1} style={{ pointerEvents: 'none' }}>
          <animate attributeName="opacity" values="0.3;0.1;0.2;0.05;0" dur="600ms" repeatCount="1" fill="freeze" />
        </path>
      )}

      {/* Hover tooltip indicator — pulsing dot at curve midpoint */}
      {isHovered && !isHighlighted && !isDimmed && (
        <circle cx={mx + nx} cy={my + ny} r={4}
          fill={isCrossZone ? sourceColor : '#71717a'} opacity={0.6}
          style={{ pointerEvents: 'none' }}>
          <animate attributeName="r" values="4;5;4" dur="1.5s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
};

export default React.memo(ConnectionLine);